import { Button, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import React from "react";

import { useCommonHooks } from "../../hooks";

import { ICustomButtonProps } from "./ICustomButtonProps";
import { ActionCode } from "../../api";
import { useAuth } from "../../contexts";

interface IDeleteButtonProps extends Omit<ICustomButtonProps, "onClick"> {
  onDelete?: () => void;
  showConfirm?: boolean;
  confirmDeleteMessage?: string;
  actions?: Array<ActionCode>;
}

export const DeleteButton: React.FunctionComponent<IDeleteButtonProps> = ({
  minimal = true,
  onDelete,
  loading,
  disabled,
  showConfirm = true,
  intent = Intent.DANGER,
  confirmDeleteMessage,
  text,
  title,
  small,
  actions = [],
}) => {
  const { hasAction } = useAuth();
  const canDelete = React.useMemo(
    () => hasAction(actions),
    [actions, hasAction]
  );
  const { t, showDialog } = useCommonHooks();
  const onInnerDelete = React.useCallback(
    async (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      let ok = true;
      if (showConfirm) {
        const res = await showDialog({
          message: confirmDeleteMessage || t("ConfirmDelete"),
          title: t("Delete"),
          buttons: [
            {
              text: t("No"),
              intent: "none",
              resultType: "no",
            },
            {
              text: t("Yes"),
              intent: "danger",
              resultType: "yes",
            },
          ],
        });
        ok = res === "yes";
      }
      if (ok) {
        onDelete();
      }
    },
    [confirmDeleteMessage, onDelete, showConfirm, showDialog, t]
  );
  return (
    <Button
      tabIndex={-1}
      title={title}
      icon={IconNames.TRASH}
      minimal={minimal}
      onClick={onInnerDelete}
      text={text || (minimal ? null : t("Delete"))}
      intent={intent}
      loading={loading}
      disabled={disabled || !canDelete}
      type="button"
      small={small}
    />
  );
};
